// components/AI/aiAsk.js
// Logika MURNI seputar panggilan /api/ai/ask — tanpa React, tanpa state,
// tanpa efek samping. Dipisah dari AIChat.js supaya bagian yang paling sering
// salah (bentuk payload & pembacaan respons) bisa dibaca dan diuji sendiri.
//
// Aturan file ini: JANGAN masukkan apa pun yang menyentuh setState, api.post,
// atau DOM. Semua di sini harus fungsi yang input → output-nya bisa diprediksi.

// ── Deteksi FOLLOW-UP (chart/insight dari HASIL sebelumnya) ──────────────────
// Follow-up = user minta visualisasi/analisis atas hasil query TERAKHIR, tanpa
// query baru. Contoh: "buatkan chart dari itu", "kasih insight", "jelaskan".
//
// Pemicunya DIPISAH jadi dua kelas karena keduanya tidak sama-kuat.
//
// VIZ — visualisasi/ringkasan. Kata-kata ini pada dirinya sendiri menyiratkan
// "olah yang sudah ada": tidak ada gunanya membuat chart dari data yang belum
// diambil. Aman memakai jalan pintas pertanyaan-pendek.
const FOLLOWUP_VIZ_RE = /\b(chart|grafik|graph|diagram|plot|pie|donut|bar\s?chart|visuali[sz]e|visualisasi(kan)?|visualkan|insight|analis[ai]s?|analy[sz]e|jelaskan|explain|ringkas|summari[sz]e|kesimpulan|conclusion)\b/i;

// COMPARE — perbandingan. AMBIGU secara mendasar, dan inilah sumber bug lapangan:
//
//   "bandingkan itu"                        → olah tabel yang ada   (follow-up)
//   "bulan jan berapa? bandingkan dgn feb"  → butuh data BARU       (SQL)
//
// Kata "bandingkan" sendirian tidak bisa membedakan keduanya; yang membedakan
// adalah ada-tidaknya RUJUKAN ke hasil sebelumnya. Karena itu kelas ini TIDAK
// mendapat jalan pintas pertanyaan-pendek.
//
// Kejadian nyata (sesi AWS Cost, 1 Agu 2026): tabel sebelumnya hanya berisi satu
// baris agregat tahunan (year=2026, total_usd=26959.88). Pertanyaan "bulan jan
// berapa? bandingkan dengan feb" panjangnya tepat 6 kata, jadi lolos lewat
// `words <= 6` dan diperlakukan sebagai follow-up. Tidak ada SQL yang jalan,
// model terpaksa menjawab dari tabel yang tidak memuat jawabannya, lalu menyebut
// angka TAHUNAN itu sebagai "Januari". Kekeliruan tsb mengendap di konteks
// percakapan dan tiga giliran berikutnya ikut salah.
const FOLLOWUP_CMP_RE = /\b(bandingkan|dibanding(kan)?|banding|compare|comparison|vs\.?|versus)\b/i;

// Kata rujukan ke hasil sebelumnya (memperkuat sinyal follow-up).
const REFERENT_RE = /\b(itu|tersebut|tadi|sebelumnya|di\s?atas|ini|that|above|previous|it|the\s?(data|result|table|chart))\b/i;

// Ambang "pertanyaan pendek" — hanya berlaku untuk kelas VIZ.
const SHORT_WORDS = 6;

export function isFollowUpQuery(text) {
  const s = String(text || '');
  const isViz = FOLLOWUP_VIZ_RE.test(s);
  const isCmp = FOLLOWUP_CMP_RE.test(s);
  if (!isViz && !isCmp) return false;

  // Rujukan eksplisit ("itu", "tersebut", "di atas") selalu cukup, untuk kedua
  // kelas: user secara harfiah menunjuk hasil yang sudah ada.
  if (REFERENT_RE.test(s)) return true;

  // Tanpa rujukan, hanya kelas VIZ yang boleh lewat jalan pintas pendek.
  // Perbandingan tanpa rujukan JATUH KE SQL — lebih baik satu query tambahan
  // daripada jawaban percaya diri yang dirakit dari tabel yang salah.
  if (isViz) return s.trim().split(/\s+/).length <= SHORT_WORDS;
  return false;
}

// Ambil pesan assistant TERAKHIR yang membawa data tabel (tableRows).
export function lastRowsFrom(messages) {
  for (let i = (messages || []).length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role === 'assistant' && Array.isArray(m.tableRows) && m.tableRows.length) {
      return { rows: m.tableRows, question: null };
    }
  }
  return null;
}

// Estimasi kasar bila provider tidak mengirim usage: 1 token ≈ 3,5 char.
export function estimateTokens(text) {
  if (!text) return 0;
  return Math.round(String(text).length / 3.5);
}

// Apakah halaman ini menyediakan ringkasan data live (Opsi B)?
export function hasPageContextOf(pageContext) {
  return !!pageContext && pageContext.summary != null && pageContext.summary !== '';
}

// Halaman dashboard yang tak terdaftar sebagai source SQL tidak bisa dilayani.
// Dikecualikan: mode General (chat umum), mode Coding, dan halaman LIVE yang
// menyediakan pageContext.
export function isUnservablePage({ isGeneral, isCode, sqlSchema, hasPageContext }) {
  return !isGeneral && !isCode && sqlSchema?.mode !== 'sql' && !hasPageContext;
}

// Perlukah auto-detect dashboard dari isi pertanyaan?
// Mode coding: TIDAK PERNAH. Tanpa guard ini, "kenapa query AWS Cost ini
// lambat?" akan dibelokkan router ke source aws_cost dan dijawab lewat
// text-to-SQL, bukan sebagai pertanyaan kode.
export function needsAutoDetect({ isCode, sqlSchema, hasPageContext, isAutoPicked }) {
  if (isCode) return false;
  return (sqlSchema?.mode !== 'sql' && !hasPageContext) || !!isAutoPicked;
}

// Resolve sentinel dropdown ('custom'/'auto') → ID model ASLI di
// config.customModel. Tanpa ini, provider spt Bedrock/Ollama yang dropdown-nya
// 'custom' akan mengirim literal string "custom" sbg modelId (invalid).
// Mencerminkan resolveModel di aiProxy.js.
export function resolveModelId(config) {
  return (config.model === 'custom' || config.model === 'auto') && config.customModel
    ? config.customModel
    : config.model;
}

// Tentukan payload mode. URUTAN PRIORITASNYA MENGIKAT:
//   code > context > followup > (tanpa mode)
// Coding tidak pernah memakai context/followup; halaman LIVE non-SQL tidak
// punya rows hasil SQL sehingga context menang atas follow-up.
export function buildModePayload({
  isCode, hasPageContext, sqlSchema, pageContext, activeLabel,
  followUpOn, userText, messages,
}) {
  if (isCode) return { mode: 'code' };

  // [SQL-WHEN 2026-10-28] Halaman preferContext boleh menyerahkan pertanyaan
  // tertentu ke text-to-SQL lewat pageContext.sqlWhen (RegExp) — mis. riwayat
  // perubahan/resize yang tidak ada di ringkasan halaman.
  const sqlOverride = sqlSchema?.mode === 'sql' && pageContext?.sqlWhen instanceof RegExp
    && pageContext.sqlWhen.test(String(userText || ''));
  if (hasPageContext && !sqlOverride && (sqlSchema?.mode !== 'sql' || pageContext?.preferContext)) {
    return {
      mode: 'context',
      context: pageContext.summary,
      context_label: pageContext.label || activeLabel || undefined,
    };
  }

  if (followUpOn && isFollowUpQuery(userText)) {
    const prev = lastRowsFrom(messages);
    if (prev && prev.rows) {
      return { mode: 'followup', prev_rows: prev.rows.slice(0, 200) };   // batasi ukuran payload
    }
  }

  return null;
}

// Rakit body request /api/ai/ask.
export function buildAskBody({
  effSource, userText, sessionId, config, resolvedModel, lang,
  modePayload, imageParts,
}) {
  return {
    source:     effSource,               // dashboard aktif / hasil auto-detect / '__general__'
    question:   userText,
    session_id: sessionId,               // kontinuitas konteks
    provider:   config.provider,         // dihormati bila AI Service punya key-nya
    model:      resolvedModel,           // ID model ASLI (bukan sentinel 'custom')
    lang,                                // bahasa eksplisit dari useLang (id/en)
    ...(modePayload || {}),              // mode:'code'|'context'|'followup'
    // Gambar hanya ikut di giliran ini — AI Service sengaja TIDAK menyimpannya
    // ke riwayat sesi agar token tidak berlipat.
    ...(imageParts && imageParts.length ? { images: imageParts } : {}),
  };
}

// Token dari AI Service (usage asli provider). Bila tidak dikirim (jarang),
// jatuh ke estimasi kasar dan ditandai `estimated`.
export function normalizeUsage(usage, userText, reply) {
  const u = usage || {};
  const prompt     = u.prompt_tokens     || u.input_tokens  || 0;
  const completion = u.completion_tokens || u.output_tokens || 0;
  const total      = u.total_tokens      || (prompt + completion);
  if (total) return { prompt, completion, total, estimated: false };
  const p = estimateTokens(userText);
  const c = estimateTokens(reply);
  return { prompt: p, completion: c, total: p + c, estimated: true };
}

// Mode jawaban untuk chip transparansi di baris meta. Tanpa penanda ini tidak
// ada cara melihat apakah sebuah jawaban memakai konteks percakapan atau
// digenerate ulang sebagai pertanyaan baru — sumber kebingungan "kok chat saya
// tidak nyambung".
export function answerModeOf(r, modePayload) {
  if (/^--\s*deterministik/i.test(String(r?.sql || ''))) return 'deterministic';
  if (modePayload?.mode === 'followup') return 'followup';
  if (modePayload?.mode === 'context')  return 'context';
  return r?.sql ? 'sql' : 'chat';
}

// Label sumber sesuai MODE AKTUAL respons (bukan selalu text-to-SQL):
// ada sources → Knowledge Base (RAG); ada sql → text-to-SQL; selain itu → chat.
// `labels` dilewatkan dari pemanggil supaya file ini tetap bebas i18n.
export function dataSourceLabel(r, labels) {
  if (Array.isArray(r?.sources) && r.sources.length) return labels.rag;
  return r?.sql ? labels.sql : labels.chat;
}
